import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useSectionContent } from '@/hooks/useSectionContent';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import { normalizeCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

interface PortfolioItem {
  id: number;
  titre: string;
  description: string;
  detailssup: string | null;
  product_image: string;
  images: string | null;
  video: string | null;
  prix: number;
  tags: string | null;
  pays: string | null;
  ville: string | null;
  categorie_id: number | null;
  created_at: string;
}

interface PortfolioCategory {
  id: number;
  nom: string;
  parent_id: number | null;
}

export default function PortfolioPublic() {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [categories, setCategories] = useState<PortfolioCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [detailItem, setDetailItem] = useState<PortfolioItem | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tenant, isTenant } = useTenant();
  const { before, highlight, subtitle } = useSectionContent('portfolio');
  // Suivi de visite de la page portfolio publique (vues, IP, géo, provenance).
  useThemePageTracking('portfolio', tenant?.id ?? null, isTenant && !!tenant);
  // Ouverture automatique d'un projet depuis l'accueil (?item=<id>).
  const [searchParams] = useSearchParams();
  const openItemId = Number(searchParams.get('item') || 0);
  const autoOpenedRef = useRef(false);
  // Prix affichés dans la devise choisie par le visiteur (taux du jour).
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const { display } = useDisplayCurrency(monaie);

  useEffect(() => {
    setLoading(true);
    let query = supabase
      .from('portfolio')
      .select('*');
    
    if (isTenant && tenant) {
      query = query.eq('idcommerce', tenant.id);
    }
    
    query.order('created_at', { ascending: false })
      .then(({ data, error: fetchError }) => {
        if (fetchError) setError(fetchError.message);
        else setItems(data || []);
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [isTenant, tenant]);

  // Si l'URL contient ?item=<id>, on ouvre directement la fiche du projet cliqué
  // depuis la page d'accueil (après le chargement de la liste).
  useEffect(() => {
    if (autoOpenedRef.current || !openItemId || items.length === 0) return;
    const target = items.find((it) => it.id === openItemId);
    if (target) {
      setDetailItem(target);
      autoOpenedRef.current = true;
    }
  }, [openItemId, items]);

  // Catégories de service du commerce : servent de filtres sur le portfolio public.
  useEffect(() => {
    if (!isTenant || !tenant) { setCategories([]); return; }
    let cancelled = false;
    supabase
      .from('categorieservices')
      .select('id, nom, parent_id, sort_order')
      .eq('idcommerce', tenant.id)
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true })
      .then(({ data }) => { if (!cancelled) setCategories((data as PortfolioCategory[]) || []); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [isTenant, tenant]);

  const catNameMap = new Map(categories.map((c) => [c.id, c.nom]));

  // Galerie d'images additionnelles d'un projet (stockée séparée par « | »).
  const galleryOf = (item: PortfolioItem): string[] =>
    (item.images || '').split('|').map((v) => v.trim()).filter(Boolean);
  // Les catégories qui ont au moins un projet sont proposées comme filtres.
  const usedCategoryIds = [...new Set(items.map((it) => it.categorie_id).filter((id): id is number => !!id))];
  const filterCategories = categories.filter((c) => usedCategoryIds.includes(c.id));
  const visibleItems = activeCategory
    ? items.filter((it) => it.categorie_id === activeCategory)
    : items;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
            {before}
            {before && highlight ? ' ' : ''}
            {highlight && <span className="text-primary-500">{highlight}</span>}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 md:py-12">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {filterCategories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeCategory === null
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-apps-2-line"></i>
                Tout
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === null ? 'bg-white/25' : 'bg-background-200/70 text-foreground-600'}`}>{items.length}</span>
              </button>
              {filterCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-price-tag-3-line"></i>
                  {cat.nom}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === cat.id ? 'bg-white/25' : 'bg-background-200/70 text-foreground-600'}`}>
                    {items.filter((it) => it.categorie_id === cat.id).length}
                  </span>
                </button>
              ))}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
              <p className="text-foreground-500">{error}</p>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-briefcase-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">Aucun projet pour le moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setDetailItem(item)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setDetailItem(item); }}
                  role="button"
                  tabIndex={0}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 cursor-pointer group"
                >
                  <div className="h-48 bg-background-100 overflow-hidden relative">
                    <img
                      src={item.product_image || 'https://readdy.ai/api/search-image?query=Professional%20portfolio%20project%20showcase%20with%20clean%20modern%20aesthetic%20soft%20gradient%20background%20abstract%20geometric%20design%20corporate%20style&width=600&height=400&seq=public-portfolio-01&orientation=landscape'}
                      alt={item.titre}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimalist%20placeholder%20with%20soft%20neutral%20gradient%20background%20professional%20clean%20aesthetic&width=600&height=400&seq=public-portfolio-fallback&orientation=landscape';
                      }}
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1.5 pointer-events-none">
                      {galleryOf(item).length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-black/55 text-white text-[10px] font-semibold flex items-center gap-1">
                          <i className="ri-image-2-line"></i>
                          {galleryOf(item).length}
                        </span>
                      )}
                      {item.video && (
                        <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-semibold flex items-center gap-1">
                          <i className="ri-play-circle-fill"></i>
                          Vidéo
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-base font-semibold text-foreground-900">{item.titre}</h3>
                      {item.prix > 0 && (
                        <span className="text-sm font-bold text-primary-600 whitespace-nowrap flex-shrink-0">{display(item.prix, monaie)}</span>
                      )}
                    </div>
                    {item.categorie_id && catNameMap.get(item.categorie_id) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium mb-2">
                        <i className="ri-price-tag-3-line text-[10px]"></i>
                        {catNameMap.get(item.categorie_id)}
                      </span>
                    )}
                    <p className="text-sm text-foreground-500 mb-3 line-clamp-2">{item.description}</p>
                    {item.tags && (
                      <div className="flex items-center gap-1.5 flex-wrap mb-3">
                        {item.tags.split(',').map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded-full text-xs">{tag.trim()}</span>
                        ))}
                      </div>
                    )}
                    {item.detailssup && (
                      <p className="text-xs text-foreground-400 mt-2 line-clamp-3">{item.detailssup}</p>
                    )}
                    <div className="mt-3 pt-3 border-t border-background-200/70">
                      <span className="text-xs text-foreground-400">
                        <i className="ri-map-pin-line mr-1"></i>
                        {item.ville && item.pays ? `${item.ville}, ${item.pays}` : item.ville || item.pays || 'Non spécifié'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Fiche détail d'un projet (cover + galerie + vidéo) */}
      {detailItem && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 md:p-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetailItem(null)}></div>
          <div className="relative bg-background-50 rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="text-base md:text-lg font-bold font-heading text-foreground-950 truncate">{detailItem.titre}</h3>
                {detailItem.categorie_id && catNameMap.get(detailItem.categorie_id) && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium">
                    <i className="ri-price-tag-3-line text-[10px]"></i>
                    {catNameMap.get(detailItem.categorie_id)}
                  </span>
                )}
              </div>
              <button
                onClick={() => setDetailItem(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-foreground-800 hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="overflow-y-auto p-5 space-y-5">
              {/* Cover */}
              {detailItem.product_image && (
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-100 cursor-pointer" onClick={() => setFullscreenImage(detailItem.product_image)}>
                  <img src={detailItem.product_image} alt={detailItem.titre} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Vidéo */}
              {detailItem.video && (
                <div>
                  <h5 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2">Vidéo</h5>
                  <video src={detailItem.video} controls className="w-full rounded-lg bg-black aspect-video" />
                </div>
              )}

              {/* Galerie */}
              {galleryOf(detailItem).length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2">Galerie</h5>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                    {galleryOf(detailItem).map((url) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setFullscreenImage(url)}
                        className="aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200/70 cursor-pointer hover:border-primary-300 transition-colors"
                      >
                        <img src={url} alt={detailItem.titre} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {detailItem.prix > 0 && (
                <span className="text-xl font-bold text-primary-600 block">
                  {display(detailItem.prix, monaie)}
                </span>
              )}

              {detailItem.description && (
                <div>
                  <h5 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2">Description</h5>
                  <p className="text-sm text-foreground-700 leading-relaxed">{detailItem.description}</p>
                </div>
              )}

              {detailItem.detailssup && (
                <div>
                  <h5 className="text-xs font-semibold text-foreground-400 uppercase tracking-wide mb-2">Détails</h5>
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap">{detailItem.detailssup}</p>
                </div>
              )}

              {detailItem.tags && (
                <div className="flex items-center gap-2 flex-wrap">
                  {detailItem.tags.split(',').map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-secondary-50 text-secondary-700 rounded-full text-xs">{tag.trim()}</span>
                  ))}
                </div>
              )}

              {(detailItem.ville || detailItem.pays) && (
                <div className="pt-3 border-t border-background-200/70">
                  <span className="text-xs text-foreground-400">
                    <i className="ri-map-pin-line mr-1"></i>
                    {[detailItem.ville, detailItem.pays].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse plein écran */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <img
            src={fullscreenImage}
            alt="Vue agrandie"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}